import type { ReviewItem } from '@/hooks/useTableColumns'
import { getMaterialList } from '@/services/apis/aiwriter'
import type { MaterialListQueryParams, MaterialListItem, MaterialListResponse } from '@/services/interface'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useModel } from '@umijs/max'

dayjs.extend(utc)
dayjs.extend(timezone)

const TIMEZONE = 'America/New_York'

// ---------- Shared mapper (same as hook default) ----------

const STATUS_MAP_OUTPUT: Record<string, ReviewItem['status']> = {
  pending_review: 'pending_review',
  in_review: 'in_review',
  reviewed: 'reviewed',
  published: 'published',
  abandoned: 'abandoned',
}

// WriterTable 已确认后端时间字段固定为：
// - createTime: 创建时间（秒级）
// - updateTime: 最后更新时间（秒级）
const toMsFromSeconds = (value: unknown): number | undefined => {
  if (value === undefined || value === null) return undefined
  const num = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(num)) return undefined
  return num * 1000
}

const mapStatus = (rawStatus: string): ReviewItem['status'] => {
  const normalizedStatus = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : 'pending_review'
  return STATUS_MAP_OUTPUT[normalizedStatus] ?? 'pending_review'
}

export const defaultMapMaterialToReviewItem = (item: MaterialListItem, index: number): ReviewItem => {
  const safeItem: MaterialListItem = item ?? {} as MaterialListItem
  const id = String(safeItem.id ?? safeItem.materialId ?? index + 1)
  const title = safeItem.title && String(safeItem.title).trim() ? String(safeItem.title) : '-'
  // createTime / updateTime 为秒级
  const createdAtMs = toMsFromSeconds(item?.createTime)
  const updatedAtMs = toMsFromSeconds(item?.updateTime)
  const createdAt = createdAtMs ? dayjs(createdAtMs).toISOString() : '-'
  const updatedAt = updatedAtMs ? dayjs(updatedAtMs).toISOString() : '-'
  const rawStatus = String(safeItem.status ?? 'pending_review')
  const status = mapStatus(rawStatus)
  const writtenBy = safeItem.editorName ?? '-'
  const reviewedBy = safeItem.reviewerName ?? (status === 'reviewed' || status === 'published' ? '-' : undefined)

  return {
    id,
    title,
    createdAt,
    updatedAt,
    status,
    writtenBy,
    reviewedBy,
  }
}

interface MaterialListModelState {
  dataSource: ReviewItem[]
  total: number
  loading: boolean
  page: number
  pageSize: number
  tab?: MaterialListQueryParams['tab']
  editorId?: string
  keyword?: string
  authorName?: string
  startTime?: number
  endTime?: number
}

interface UseMaterialListModelResult {
  dataSource: ReviewItem[]
  total: number
  loading: boolean
  page: number
  pageSize: number
  tab?: MaterialListQueryParams['tab']
  editorId?: string
  keyword?: string
  authorName?: string
  startTime?: number
  endTime?: number
  pagination: any
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setTab: (tab?: MaterialListQueryParams['tab']) => void
  setEditorId: (editorId?: string) => void
  setKeyword: (keyword?: string) => void
  setAuthorName: (authorName?: string) => void
  setMapper: (mapper?: (raw: MaterialListItem, index: number) => ReviewItem) => void
  setState: (state: MaterialListModelState | ((prevState: MaterialListModelState) => MaterialListModelState)) => void
  loadMaterials: () => Promise<void>
  startPolling: (interval?: number) => void
  stopPolling: () => void
  setStartTime: (startTime?: number) => void
  setEndTime: (endTime?: number) => void
}

// 默认分页配置
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 10

// 计算默认时间范围：最近一周
const getDefaultTimeRange = () => {
  const now = dayjs().tz(TIMEZONE)
  const oneWeekAgo = now.subtract(7, 'day')
  const startDateTime = oneWeekAgo.startOf('day')
  const endDateTime = now.endOf('day')
  return {
    startTime: Math.floor(startDateTime.valueOf() / 1000),
    endTime: Math.floor(endDateTime.valueOf() / 1000),
  }
}

const useMaterialListModel = (): UseMaterialListModelResult => {
  // 初始化默认时间范围
  const defaultTimeRange = getDefaultTimeRange()
  
  const [state, setState] = useState<MaterialListModelState>({
    dataSource: [],
    total: 0,
    loading: false,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    tab: 'pending',
    editorId: undefined,
    keyword: undefined,
    authorName: undefined,
    startTime: defaultTimeRange.startTime,
    endTime: defaultTimeRange.endTime,
  })

  const mapperRef = useRef<(raw: MaterialListItem, index: number) => ReviewItem>(defaultMapMaterialToReviewItem)
  const isFetchingRef = useRef<boolean>(false)
  const mountedRef = useRef<boolean>(true)
  const pollingTimerRef = useRef<number | null>(null)
  // 记录当前轮询间隔，便于依赖变化时重启定时器
  const pollingIntervalRef = useRef<number>(10000)

  const { searchValue } = useModel('pageModel') as { searchValue: string }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }
  }, [])

  const setPage = (page: number) => setState((s) => ({ ...s, page }))
  const setPageSize = (pageSize: number) => setState((s) => ({ ...s, pageSize }))
  const resetPagination = (partial: Partial<MaterialListModelState> = {}) => ({
    ...partial,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const setTab = (tab?: MaterialListQueryParams['tab']) =>
    setState((s) => ({ ...s, ...resetPagination({ tab }) }))
  const setEditorId = (editorId?: string) =>
    setState((s) => ({ ...s, ...resetPagination({ editorId }) }))
  const setKeyword = (keyword?: string) =>
    setState((s) => ({ ...s, ...resetPagination({ keyword }) }))
  const setAuthorName = (authorName?: string) => setState((s) => ({ ...s, authorName }))
  const setStartTime = (startTime?: number) =>
    setState((s) => ({ ...s, ...resetPagination({ startTime }) }))
  const setEndTime = (endTime?: number) =>
    setState((s) => ({ ...s, ...resetPagination({ endTime }) }))
  const setMapper = (mapper?: (raw: MaterialListItem, index: number) => ReviewItem) => {
    mapperRef.current = mapper || defaultMapMaterialToReviewItem
  }

  // 已直接使用后端 tab，移除 activeStatus

  const clearPolling = () => {
    if (pollingTimerRef.current) {
      window.clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }

  // 基础拉取：显式参数优先，避免依赖 state 造成的异步延迟
  // 获取审核列表数据并写入 dataSource（可静默，不改 loading）
  const loadReviewList = useCallback(
    async (params: { tab?: MaterialListQueryParams['tab']; page: number; size: number; keyword: string; editorId?: string; startTime?: number; endTime?: number; silent?: boolean }): Promise<void> => {
      if (isFetchingRef.current) return
      isFetchingRef.current = true
      // 只有在非静默模式下才显示loading状态
      if (!params.silent) {
        setState((s) => ({ ...s, loading: true }))
      }
      try {
        // 构建请求参数，只有当 editorId 有值时才包含
        const requestParams: MaterialListQueryParams = {
          tab: params.tab,
          page: params.page,
          size: params.size,
          keyword: params.keyword,
        }
        
        // 只有当 editorId 有值时才添加到请求参数中
        if (params.editorId) {
          requestParams.editorId = params.editorId
        }
        
        // 只有当 startTime 有值时才添加到请求参数中
        if (params.startTime) {
          requestParams.startTime = params.startTime
        }
        
        // 只有当 endTime 有值时才添加到请求参数中
        if (params.endTime) {
          requestParams.endTime = params.endTime
        }
        
        const {list, total}: MaterialListResponse = await getMaterialList(requestParams)
        const listData: MaterialListItem[] = Array.isArray(list) ? list : []
        if (mountedRef.current) {
          const mapped = listData.map(mapperRef.current)
          // 更新数据源
          setState((s) => ({ ...s, dataSource: mapped }))
          // 仅当后端返回有效 total 时再更新 total
          const apiTotal = typeof total === 'number' ? total : undefined
          if (typeof apiTotal === 'number') {
            setState((s) => ({ ...s, total: apiTotal }))
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to obtain the material list:', e)
        if (mountedRef.current) {
          setState((s) => ({ ...s, dataSource: [] }))
        }
      } finally {
        if (mountedRef.current) {
          // 只有在非静默模式下才清除loading状态
          if (!params.silent) {
            setState((s) => ({ ...s, loading: false }))
          }
        }
        isFetchingRef.current = false
      }
    },
    [],
  )

  // 兼容旧用法：默认以当前 state 发起
  const loadMaterials = useCallback(async (): Promise<void> => {
    return loadReviewList({
      tab: state.tab,
      page: state.page,
      size: state.pageSize,
      keyword: state.keyword || searchValue,
      editorId: state.editorId,
      startTime: state.startTime,
      endTime: state.endTime,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tab, state.page, state.pageSize, state.keyword, searchValue, state.editorId, state.startTime, state.endTime, loadReviewList])

  // 静默拉取：用于轮询，不显示loading状态
  const loadMaterialsSilent = useCallback(async (): Promise<void> => {
    return loadReviewList({
      tab: state.tab,
      page: state.page,
      size: state.pageSize,
      keyword: state.keyword || searchValue,
      editorId: state.editorId,
      startTime: state.startTime,
      endTime: state.endTime,
      silent: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tab, state.page, state.pageSize, state.keyword, searchValue, state.editorId, state.startTime, state.endTime, loadReviewList])

  const startPolling = useCallback(
    (interval: number = 10000) => {
      clearPolling()
      pollingIntervalRef.current = interval
      // 立即执行一次（非静默，显示loading）
      loadMaterials()
      // 然后进入定时轮询（静默，不显示loading）
      pollingTimerRef.current = window.setInterval(loadMaterialsSilent, interval)
    },
    [loadMaterials, loadMaterialsSilent],
  )

  const stopPolling = useCallback(() => {
    clearPolling()
  }, [])

  // 当查询参数变化时，如果处于轮询中，则清除旧定时器并用最新参数重启
  useEffect(() => {
    if (pollingTimerRef.current) {
      loadMaterials()
      clearPolling()
      // 直接按最新参数重启定时器（使用静默拉取，避免重复请求）
      // 轮询会使用最新的参数，包括 editorId、startTime、endTime
      pollingTimerRef.current = window.setInterval(loadMaterialsSilent, pollingIntervalRef.current)
    }
  }, [state.tab, state.page, state.pageSize, state.keyword, searchValue, state.editorId, state.startTime, state.endTime, loadMaterials, loadMaterialsSilent])

  return {
    // state
    dataSource: state.dataSource,
    total: state.total,
    loading: state.loading,
    page: state.page,
    pageSize: state.pageSize,
    tab: state.tab,
    editorId: state.editorId,
    keyword: state.keyword,
    authorName: state.authorName,
    startTime: state.startTime,
    endTime: state.endTime,
    pagination: {
      current: state.page,
      pageSize: state.pageSize,
      total: state.total,
      position: ['bottomLeft'] as any,
      showSizeChanger: false,
      showQuickJumper: true,
      onChange: (newPage: number, newSize?: number) => {
        // 先更新本地分页状态
        setPage(newPage)
        const finalSize = newSize && newSize !== state.pageSize ? newSize : state.pageSize
        if (finalSize !== state.pageSize) {
          setPageSize(finalSize)
        }
      },
      onShowSizeChange: (newPage: number, newSize: number) => {
        setPage(newPage)
        setPageSize(newSize)
      },
    },
    // actions
    setPage,
    setPageSize,
    setTab,
    setEditorId,
    setKeyword,
    setAuthorName,
    setMapper,
    setState,
    loadMaterials,
    startPolling,
    stopPolling,
    setStartTime,
    setEndTime,
  }
}

export default useMaterialListModel